"use client" // Ensure this is a client component
import { useState, useEffect, useRef } from "react"
import AOS from "aos"
import "aos/dist/aos.css"
import "./WhyusOverviewSection.css"

const whyusTabs = [
  { label: "WHY US", id: "whyus" },
  { label: "TEAM", id: "team" },
  { label: "IMPLEMENTATION", id: "implementation" },
  { label: "CONSULTANCY", id: "consultancy" },
  { label: "GROW & SCALE", id: "grow-and-scale" }, // Changed ID for consistency
  { label: "CONTROL", id: "control" },
  { label: "PARTNERSHIP", id: "partnership" },
]

const features = [
  {
    icon: "/icons/process-oriented.webp",
    title: "HIGHLY PROCESS-ORIENTED",
    description:
      "We follow battle-tested, unique elite processes for everything that we do, ensuring high-quality, efficient, and debt-free delivery.",
  },
  {
    icon: "/icons/ownership.webp",
    title: "TECHNICAL EXCELLENCY",
    description:
      "Our agile engineering practices will bring software craftsmanship, quality, and consistency to the table, all in a flexible state to guarantee ever-changing requirements.",
  },
  {
    icon: "/icons/collaboration.webp",
    title: "WORK WITH THE 1%",
    description:
      "Best-in-class Europe-based developers with years of experience across different industries, using top processes to deliver high-quality products.",
  },
  {
    icon: "/build.webp",
    title: "BUILT TO SCALE",
    description: "We believe in building future-proof technology, meaning we will design today thinking of tomorrow.",
  },
  {
    icon: "/icons/vertcal-expertise.webp",
    title: "VERTICAL EXPERTISE",
    description:
      "Specialized knowledge of industries allows us to deliver well-tailored solutions, addressing unique challenges and achieving impactful results.",
  },
  {
    icon: "/acceleration.webp",
    title: "DIGITAL ACCELERATION WITH A LEAN MINDSET",
    description: "We focus on doing the right things to fuel your growth, nothing more, nothing less.",
  },
  {
    icon: "/transparency.webp",
    title: "FULL TRANSPARENCY",
    description: "We provide transparent and clear communication, project management, and budgeting.",
  },
  {
    icon: "/icons/respect.webp",
    title: "PARTNER & ADVISOR",
    description:
      "We take strong ownership of the projects we work on, meaning you can expect a business partner ready to help you every step of the way.",
  },
  {
    icon: "/human.webp",
    title: "HUMAN-CENTRIC",
    description:
      "We build software and build ourselves with the people at heart, ensuring seamless solutions to the problems we solve.",
  },
]

const WhyusOverviewSection = () => {
  const [active, setActive] = useState("whyus")
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef(null)
  const navbarRef = useRef(null) // Ref for the sticky navbar

  useEffect(() => {
    AOS.init({ duration: 800, once: true })

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      },
    )
    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    const handleScroll = () => {
      const currentNavbarHeight = navbarRef.current ? navbarRef.current.offsetHeight : 0
      // Offset for scroll detection: navbar height + a small buffer
      const scrollOffset = currentNavbarHeight + 20

      // console.log("--- Scroll Event ---");
      // console.log("window.scrollY:", window.scrollY);
      // console.log("Current Navbar Height:", currentNavbarHeight);
      // console.log("Calculated Scroll Offset:", scrollOffset);

      let foundActive = false
      for (let i = whyusTabs.length - 1; i >= 0; i--) {
        const tabId = whyusTabs[i].id
        const section = document.getElementById(tabId)
        if (section) {
          // console.log(`Section ${tabId} offsetTop:`, section.offsetTop);
          if (section.offsetTop <= window.scrollY + scrollOffset) {
            setActive(tabId)
            foundActive = true
            // console.log("Active section set to:", tabId);
            break
          }
        } else {
          // console.warn(`Section with ID '${tabId}' not found.`);
        }
      }
      if (!foundActive) {
        // If no section is in view (e.g., at the very top of the page before any target sections)
        setActive(whyusTabs[0].id)
      }
      // console.log("--------------------");
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll() // Call once to set initial state on load

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const handleClick = (id) => {
    setActive(id) // Set active state immediately on click
    const section = document.getElementById(id)
    if (section && navbarRef.current) {
      const currentNavbarHeight = navbarRef.current.offsetHeight
      // Scroll to the section, adjusting for the navbar's height
      window.scrollTo({
        top: section.offsetTop - currentNavbarHeight,
        behavior: "smooth",
      })
      // console.log(`Clicked ${id}, scrolling to:`, section.offsetTop - currentNavbarHeight);
    }
  }

  return (
    <>
     <nav className="whyusoverview-sticky-navbar" ref={navbarRef}>
      <div className="container">
        <ul>
          {whyusTabs.map((tab) => (
            <li key={tab.id}>
              <a
                onClick={() => handleClick(tab.id)}
                className={active === tab.id ? "active" : ""}
                // href={"#" + tab.id} // Removed href to prevent default jump and rely on JS for smooth scroll
              >
                {tab.label}
              </a>
            </li>
          ))}
        </ul>
        </div>
      </nav>
    <section
      ref={sectionRef}
      className={`whyusoverview-section ${isVisible ? "animate" : ""}`}
      id="whyus" // This section itself is the target for 'whyus' tab
    >
      
      {/* Sticky Navigation Bar */}
     
      {/* Content Wrapper */}
      <div className="whyusoverview-content-wrapper">
        {/* Main Heading Area (part of 'whyus' section) */}
        <div className="whyusoverview-header">
          <div className="whyusoverview-heading">
            <h1>
              <span className="whyusoverview-heading-part1">WHAT SETS US</span>
              <span className="whyusoverview-heading-part2">APART</span>
            </h1>
          </div>
          <div className="whyusoverview-intro">
            <p>
              Lattice Labs partners with Enterprises and Scale-Ups on their digital journey, delivering tailored
              software solutions through elite processes and expert consultants who work closely with client teams to
              strengthen their competitive edge.
            </p>
          </div>
        </div>
        {/* Features Grid (part of 'whyus' section) */}
        <div className="whyusoverview-features-grid">
          {features.map((feature, idx) => (
            <div className="whyusoverview-feature-block" key={idx}>
              <div className="whyusoverview-icon">
                {" "}
                <img src={feature.icon || "/placeholder.svg"} alt={feature.title + " icon"} />
              </div>
              <h3 className="whyusoverview-feature-title">{feature.title}</h3>
              <p className="whyusoverview-feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
        {/* Bottom Text (part of 'whyus' section) */}
        <div className="whyusoverview-bottom-text">
          <p>
            At Lattice Labs we provide a unique service tailored <br/>to the needs of companies that are focused on digital
            growth.
          </p>
        </div>
      </div>

      {/* Placeholder Sections for other tabs */}
     
     
    </section>
    </>
  )
}

export default WhyusOverviewSection
